import { defineStore } from "pinia";
import { RUOLI } from "~~/shared/utils/ruoli";
import { inizialiUtente, srcAvatar } from "~/utils/avatar";
import { normalizzaMenu } from "~/utils/menu";
import { menuDelRuolo } from "~/utils/menuRuolo";

export interface ProfiloUtente {
  id?: number;
  id_utente?: number;
  nome?: string;
  cognome?: string;
  username?: string;
  email?: string | null;
  telefono?: string | null;
  // data di nascita come arriva dal server («aaaa-mm-gg» o ISO)
  data_nascita?: string | null;
  // 2FA attivo (0/1 dal database)
  otp_enabled?: number | boolean | null;
  // Solo per i clienti: `base` o `esperto` (decide quanto dettaglio mostra la dashboard)
  livello_competenza?: "base" | "esperto";
  id_ruolo?: number;
  immagine_mime?: string | null;
  immagine_base64?: string | null;
}

interface RispostaProfilo {
  success?: boolean;
  error?: string;
  data?: ProfiloUtente;
}

// Sessione dell'utente: ruolo e menu di navigazione (il menu sta nel codice, lo sceglie il ruolo), profilo (nome, ruolo, foto) e uscita. Lo stato condiviso tra
// sidebar, pagine e guardie sta qui; le chiamate al server passano da `useAuthFetch`.
export const useAuthStore = defineStore("auth", () => {
  // Presi nel contesto di setup: dentro le azioni useNuxtApp() non è garantito.
  const nuxtApp = useNuxtApp();
  const { $localStorage } = nuxtApp;
  const { authFetch } = useAuthFetch();
  const nomeCookieToken = `token${useRuntimeConfig().public.base_progetto}`;

  // Ruolo salvato al login: serve prima che arrivi il profilo (guardia delle rotte, primo disegno della sidebar). Il profilo, appena
  // caricato, ha la precedenza. Del menu non si salva niente: lo ricava dal ruolo `menuDelRuolo`, quindi segue sempre il rilascio.
  const ruoloSalvato = ref<number | null>(leggiRuoloSalvato());
  const profilo = ref<ProfiloUtente | null>(null);
  // Condivisa con la pagina del profilo (che la aggiorna al cambio foto) e persistita dal plugin watch-state
  const foto = useState<string | null>("foto_profilo");

  const ruolo = computed<number | null>(() => profilo.value?.id_ruolo ?? ruoloSalvato.value);
  const menu = computed(() => menuDelRuolo(ruolo.value));
  const menuVisibile = computed(() => normalizzaMenu(menu.value));
  const avatarSrc = computed(() => srcAvatar(foto.value));
  const iniziali = computed(() => inizialiUtente(profilo.value?.nome, profilo.value?.cognome));
  const username = computed(() => profilo.value?.username ?? "");
  // 2FA attivo (il database dà 0/1)
  const dueFattoriAttivo = computed(() => Number(profilo.value?.otp_enabled) === 1 || profilo.value?.otp_enabled === true);
  // Livello del cliente; chi non è cliente vede sempre il dettaglio completo (lo decide la pagina in base al ruolo)
  const livelloCompetenza = computed(() => profilo.value?.livello_competenza ?? "base");
  const amministratore = computed(() => profilo.value?.id_ruolo === RUOLI.ADMIN);
  const cliente = computed(() => profilo.value?.id_ruolo === RUOLI.PROPRIETARIO);
  // Tecnici e amministratori: vedono le parti tecniche (rete mesh, area tecnica). Comodità della UI: il confine reale è il server
  const personaleTecnico = computed(() => profilo.value?.id_ruolo === RUOLI.ADMIN || profilo.value?.id_ruolo === RUOLI.TECNICO);
  // Cliente «base»: vede una versione semplificata (niente parti per addetti ai lavori). Il livello arriva dal server, mai dal browser
  const profiloSemplice = computed(() => cliente.value && livelloCompetenza.value === "base");

  function leggiRuoloSalvato(): number | null {
    const valore = $localStorage.getItem("ruolo");
    const numero = valore === null || valore === "" ? NaN : Number(valore);
    return Number.isInteger(numero) ? numero : null;
  }

  // Dopo il login: ricorda il ruolo (e butta la copia del menu che le versioni precedenti salvavano nel browser).
  function impostaRuolo(idRuolo: number | null | undefined): void {
    ruoloSalvato.value = idRuolo === null || idRuolo === undefined ? null : Number(idRuolo);
    if (ruoloSalvato.value === null) $localStorage.removeItem("ruolo");
    else $localStorage.setItem("ruolo", ruoloSalvato.value);
    $localStorage.removeItem("menu");
  }

  // Scarica il profilo dell'utente del token. Restituisce il messaggio d'errore, `null` se è andato a buon fine.
  async function caricaProfilo(): Promise<string | null> {
    try {
      const risposta = (await authFetch("/api/utenti/get_profilo")) as RispostaProfilo;
      if (risposta?.error) return risposta.error;
      if (!risposta?.success) return null;
      const dati = risposta.data ?? {};
      profilo.value = dati;
      if (dati.id_ruolo !== undefined && dati.id_ruolo !== ruoloSalvato.value) impostaRuolo(dati.id_ruolo);
      foto.value = dati.immagine_mime && dati.immagine_base64 ? `data:${dati.immagine_mime};base64,${dati.immagine_base64}` : null;
      return null;
    } catch (errore) {
      return errore instanceof Error ? errore.message : String(errore);
    }
  }

  // Uscita: avvisa il server (che toglie il token del dispositivo), pulisce la sessione locale e porta al login. Se il server non
  // risponde si esce lo stesso. Con `perInattivita` il login spiega il perché (avviso «Inattività rilevata»).
  async function esci(perInattivita = false): Promise<void> {
    try {
      await authFetch("/api/auth/logout", { body: { username: $localStorage.getItem("username") }, ignora401: true });
    } catch {
      // senza rete si esce comunque: il token locale viene comunque cancellato
    }
    $localStorage.removeItem("username");
    $localStorage.removeItem("menu");
    $localStorage.removeItem("ruolo");
    if (perInattivita) $localStorage.setItem(AVVISO_INATTIVITA, true);
    ruoloSalvato.value = null;
    profilo.value = null;
    // Dopo gli `await` il contesto di Nuxt può essere perso: cookie e navigazione girano dentro runWithContext. Il cookie si
    // legge di nuovo qui (non da un riferimento tenuto da prima) e la navigazione sostituisce la pagina, senza «indietro».
    await nuxtApp.runWithContext(async () => {
      useCookie<string | null | undefined>(nomeCookieToken).value = undefined;
      await navigateTo("/login", { replace: true });
    });
  }

  // Aggiorna alcuni campi del profilo già caricato (es. dopo un salvataggio o l'attivazione del 2FA)
  function aggiornaProfilo(campi: Partial<ProfiloUtente>): void {
    profilo.value = { ...(profilo.value ?? {}), ...campi };
  }

  // Cambia la foto (null = rimossa): profilo e immagine mostrata in menu e pagine restano coerenti
  function impostaFoto(mime: string | null, base64: string | null): void {
    aggiornaProfilo({ immagine_mime: mime, immagine_base64: base64 });
    foto.value = mime && base64 ? `data:${mime};base64,${base64}` : null;
  }

  // Il server ha risposto 401: il token di questo browser non vale più (es. lo stesso dispositivo è entrato da un altro browser e ha
  // rinnovato il token, o la sessione è stata chiusa). Si pulisce lo stato locale SENZA avvisare il server (non c'è più niente da
  // chiudere) e si porta al login con un avviso. `scadendo` evita di farlo più volte quando partono più richieste insieme.
  let scadendo = false;
  async function sessioneScaduta(): Promise<void> {
    if (scadendo) return;
    scadendo = true;
    try {
      $localStorage.removeItem("username");
      $localStorage.removeItem("menu");
      $localStorage.removeItem("ruolo");
      $localStorage.setItem(AVVISO_SESSIONE_SCADUTA, true);
      ruoloSalvato.value = null;
      profilo.value = null;
      await nuxtApp.runWithContext(async () => {
        useCookie<string | null | undefined>(nomeCookieToken).value = undefined;
        await navigateTo("/login", { replace: true });
      });
    } finally {
      scadendo = false;
    }
  }

  return { menu, ruolo, profilo, foto, menuVisibile, avatarSrc, iniziali, username, dueFattoriAttivo, livelloCompetenza, amministratore, cliente, personaleTecnico, profiloSemplice, impostaRuolo, caricaProfilo, aggiornaProfilo, impostaFoto, sessioneScaduta, esci };
});
